// @ts-ignore: moment declaration file
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js'
import {
    Currency,
    type ErrorArrayResponse,
    type ErrorResponse,
    type FinalUnpaidAmountType,
    type Reservation,
    ReservationStatuses,
    ReservationStatusesFromAPI,
    type SearchSubmissionType,
} from './types'
import { getLanguage } from './language';
import type { ReservationFromServer } from './pwa/types';
import { getWebAppConfigValue } from './web-app-root'

export function isBeforeDay(a, b) {
    if (!moment.isMoment(a) || !moment.isMoment(b)) return false;
  
    const aYear = a.year();
    const aMonth = a.month();
  
    const bYear = b.year();
    const bMonth = b.month();
  
    const isSameYear = aYear === bYear;
    const isSameMonth = aMonth === bMonth;
  
    if (isSameYear && isSameMonth) return a.date() < b.date();
    if (isSameYear) return aMonth < bMonth;
    return aYear < bYear;
}

export function isInclusivelyAfterDay(a, b) {
    if (!moment.isMoment(a) || !moment.isMoment(b)) return false;
    return !isBeforeDay(a, b);
}

export function isFriday(date) {
    const dayOfWeek = moment(date).isoWeekday()
    // biome-ignore lint: Potential comparison of number to string
    return dayOfWeek == 5
}

export function getDefaultDateAndTime() {
    const initialLanguage = getLanguage()

    moment.tz.setDefault('Europe/Prague')

    const currentTimeInCET = moment()

    const minuteThreshold = 54

    const currentHour = currentTimeInCET.hour()
    const currentMinute = currentTimeInCET.minute()

    let defaultStartTime = '00:01'
    const defaultStartDate = moment()

    // Adjust defaultTime to the next hour if the current time is past hh:minuteThreshold
    if (currentMinute > minuteThreshold) {
        defaultStartTime = `${String((currentHour + 1) % 24).padStart(2, '0')}:00`
    // If current hour is between 00:00 and 00:minuteThreshold, set defaultTime to 00:01
    } else if (currentHour === 0){
        defaultStartTime = '00:01'
    // If the current time is between hh:00 and hh:minuteThreshold, set defaultTime to current hour:00
    } else {
        defaultStartTime = `${String(currentHour).padStart(2, '0')}:00`
    }

    // Special case: if the current time is past 23:minuteThreshold, set defaultTime to 00:01 and increment the date
    if (currentHour === 23 && currentMinute > minuteThreshold) {
        defaultStartTime = '00:01'
        defaultStartDate.add(1, 'days')
    }

    let defaultEndTime = defaultStartTime
    const defaultEndDate = defaultStartDate.clone()

    if (initialLanguage === 'cs') {
        const startHour = Number.parseInt(defaultStartTime.split(':')[0])
        const endHour = (startHour + 12) % 24
        if (startHour + 12 >= 24) {
            defaultEndDate.add(1, 'days')
        }
        if (endHour === 0) {
            defaultEndTime = '00:01'
        } else {
            defaultEndTime = `${String(endHour).padStart(2, '0')}:00`
        }

    } else {
        defaultEndDate.add(1, 'days')
    }

    return {
        defaultStartDate: defaultStartDate.format('YYYY-MM-DD'),
        defaultEndDate: defaultEndDate.format('YYYY-MM-DD'),
        defaultStartTime,
        defaultEndTime,
    }
}

export function getSearchParams() {
    const {
        defaultStartDate,
        defaultEndDate,
        defaultStartTime,
        defaultEndTime,
    } = getDefaultDateAndTime()

    const domNode = document.getElementById('react-garage-detail-search')
    const serviceBeginsAtDate = domNode?.getAttribute('reservation-begins-at-date')
    const serviceBeginsAtTime = domNode?.getAttribute('reservation-begins-at-time')
    const serviceEndsAtDate = domNode?.getAttribute('reservation-ends-at-date')
    const serviceEndsAtTime = domNode?.getAttribute('reservation-ends-at-time')

    const searchParams = new URLSearchParams(document.location.search)

    const variantA = 'map_search'
    const variantB = 'reservation_step_reservation_time'
    
    const paramLocation =     searchParams.get(variantA+'[location]')       || searchParams.get(variantB+'[location]')       || null
    const paramBeginsAtDate = searchParams.get(variantA+'[begins_at_date]') || searchParams.get(variantB+'[begins_at_date]') || serviceBeginsAtDate || defaultStartDate
    const paramBeginsAtTime = searchParams.get(variantA+'[begins_at_time]') || searchParams.get(variantB+'[begins_at_time]') || serviceBeginsAtTime || defaultStartTime
    const paramEndsAtDate =   searchParams.get(variantA+'[ends_at_date]')   || searchParams.get(variantB+'[ends_at_date]')   || serviceEndsAtDate   || defaultEndDate
    const paramEndsAtTime =   searchParams.get(variantA+'[ends_at_time]')   || searchParams.get(variantB+'[ends_at_time]')   || serviceEndsAtTime   || defaultEndTime

    return {
        paramLocation,
        paramBeginsAtDate,
        paramBeginsAtTime,
        paramEndsAtDate,
        paramEndsAtTime,
    }
}

export function getUpdatedSearchUrl({
    currentUrl,
    searchSubmission,
    variant,
    garageId,
}: {
    currentUrl: string,
    searchSubmission: SearchSubmissionType,
    variant: 'map' | 'reservation',
    garageId?: string,
}): string {
    const url = new URL(currentUrl)
    const params = url.searchParams
    const prefix = variant === 'reservation' ? 'reservation_step_reservation_time' : 'map_search'

    params.set(`${prefix}[location]`, searchSubmission.location ?? '')
    params.set(`${prefix}[begins_at_date]`, searchSubmission.startDate?.format('YYYY-MM-DD') ?? '')
    params.set(`${prefix}[begins_at_time]`, searchSubmission.startTime ?? '')
    params.set(`${prefix}[ends_at_date]`, searchSubmission.endDate?.format('YYYY-MM-DD') ?? '')
    params.set(`${prefix}[ends_at_time]`, searchSubmission.endTime ?? '')

    if (variant === 'reservation' && garageId) {
        params.set(`${prefix}[garage_id]`, garageId)
    }

    const query = params.toString()

    return `${url.pathname}${query ? `?${query}` : ''}${url.hash}`
}

export function isSearchTimeRangeValid(search: SearchSubmissionType): boolean {
    if (!search.startDate?.isValid() || !search.endDate?.isValid() || !search.startTime || !search.endTime) {
        return false
    }

    const startTime = search.startTime === '00:01' ? '00:00' : search.startTime
    const endTime = search.endTime === '00:01' ? '00:00' : search.endTime
    const supportedTime = /^(?:[01]\d|2[0-3]):00$/
    if (!supportedTime.test(startTime) || !supportedTime.test(endTime)) {
        return false
    }

    const start = moment.tz(`${search.startDate.format('YYYY-MM-DD')} ${startTime}`, 'YYYY-MM-DD HH:mm', true, 'Europe/Prague')
    const end = moment.tz(`${search.endDate.format('YYYY-MM-DD')} ${endTime}`, 'YYYY-MM-DD HH:mm', true, 'Europe/Prague')

    return start.isValid() && end.isValid() && end.isAfter(start)
}

export function getReservationLength(paramBeginsAtDate, paramBeginsAtTime, paramEndsAtDate, paramEndsAtTime): number {

    const normalisedBeginsAtTime = paramBeginsAtTime === '00:01' ? '00:00' : paramBeginsAtTime
    const normalisedEndsAtTime = paramEndsAtTime === '00:01' ? '00:00' : paramEndsAtTime
    
    const startMoment = moment(paramBeginsAtDate + " " + normalisedBeginsAtTime, ['DD.MM.YYYY HH:mm', 'YYYY-MM-DD HH:mm'])
    const endMoment = moment(paramEndsAtDate + " " + normalisedEndsAtTime, ['DD.MM.YYYY HH:mm', 'YYYY-MM-DD HH:mm'])

    const reservationLengthHours = endMoment.diff(startMoment, 'hours')

    return reservationLengthHours
}

export function getCurrency(): Currency {
    const currency = getWebAppConfigValue('currency')

    return currency === Currency.eur ? Currency.eur : Currency.czk
}

export function getDayLimits(): {
    maximumStayDays: number,
    maximumDaysInAdvance: number,
} {
    let maximumStayDays = 30
    let maximumDaysInAdvance = 180

    const webAppMaximumStayDays = getWebAppConfigValue('maximum-stay-days')
    const webAppMaximumDaysInAdvance = getWebAppConfigValue('maximum-days-in-advance')

    if (webAppMaximumStayDays) {
        maximumStayDays = Number.parseInt(webAppMaximumStayDays)
    }

    if (webAppMaximumDaysInAdvance) {
        maximumDaysInAdvance = Number.parseInt(webAppMaximumDaysInAdvance)
    }
    
    const domNodes = [
        document.getElementById('react-garage-detail-search'),
        document.getElementById('react-home-search'),
        document.getElementById('react-pwa-root'),
    ]

    for (const domNode of domNodes) {
        if (domNode) {
            if (domNode?.getAttribute('maximum-stay-days')) {
                //@ts-ignore
                maximumStayDays =  Number.parseInt(domNode.getAttribute('maximum-stay-days'))
            }
            if (domNode?.getAttribute('maximum-days-in-advance')) {
                //@ts-ignore
                maximumDaysInAdvance =  Number.parseInt(domNode.getAttribute('maximum-days-in-advance'))
            }
        }
    }

    return {
        maximumStayDays,
        maximumDaysInAdvance,
    }
}

export function getReservationStatus(state, beginsAt, endsAt) {
    if (state === ReservationStatusesFromAPI.booked && moment() > moment(endsAt)) {
        return ReservationStatuses.elapsed
    }
    if (state === ReservationStatusesFromAPI.booked && moment() < moment(beginsAt)) {
        return ReservationStatuses.booked
    }
    if (state === ReservationStatusesFromAPI.booked && moment() > moment(beginsAt) && moment() < moment(endsAt)) {
        return ReservationStatuses.current
    }
    if (state === ReservationStatusesFromAPI.allocated) {
        return ReservationStatuses.allocated
    }
    return ReservationStatuses.canceled
}

export function normalizeFinalUnpaidAmountType(value: unknown): FinalUnpaidAmountType {
    if (
        value === 'none'
        || value === 'unfinished_reservation'
        || value === 'prolongation'
        || value === 'change_unit'
    ) {
        return value
    }

    return 'none'
}

export function getReservationBasePath(
    locale: string,
    reservationsRoute: string,
    reservationStepRoute: string,
    garageSlug?: string | null,
): string {
    const slugSegment = garageSlug ? `${encodeURIComponent(garageSlug)}/` : ''

    return `/${locale}/${slugSegment}${reservationsRoute}/${reservationStepRoute}`
}

export function getGarageDetailReservationLink({
    locale,
    reservationsRoute,
    reservationStepRoute,
    garageSlug,
    garageId,
    searchSubmission,
}: {
    locale: string,
    reservationsRoute: string,
    reservationStepRoute: string,
    garageSlug?: string | null,
    garageId: string,
    searchSubmission: SearchSubmissionType,
}): string {
    const reservationBasePath = getReservationBasePath(
        locale,
        reservationsRoute,
        reservationStepRoute,
        garageSlug,
    )
    const params = new URLSearchParams({
        'reservation_step_reservation_time[begins_at_date]': searchSubmission.startDate?.format('YYYY-MM-DD') ?? '',
        'reservation_step_reservation_time[begins_at_time]': searchSubmission.startTime ?? '',
        'reservation_step_reservation_time[ends_at_date]': searchSubmission.endDate?.format('YYYY-MM-DD') ?? '',
        'reservation_step_reservation_time[ends_at_time]': searchSubmission.endTime ?? '',
        'reservation_step_reservation_time[garage_id]': garageId,
        'reservation_step_reservation_time[location]': searchSubmission.location ?? '',
    })

    return `${reservationBasePath}?${params.toString()}`
}

export function getReservationLinkNextDay(reservation: Reservation, path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    
    const startDate = moment(reservation.startDate).add(1, 'days').format('YYYY-MM-DD')
    const startTime = moment(reservation.startDate).format('HH:mm').replace('00:00', '00:01')
    const endDate = moment(reservation.endDate).add(1, 'days').format('YYYY-MM-DD')
    const endTime = moment(reservation.endDate).format('HH:mm').replace('00:00', '00:01')

    const reservationLinkNextDay = `${normalizedPath
        }?reservation_step_reservation_time%5Bbegins_at_date%5D=${
        startDate}&reservation_step_reservation_time%5Bbegins_at_time%5D=${
        startTime}&reservation_step_reservation_time%5Bends_at_date%5D=${
        endDate}&reservation_step_reservation_time%5Bends_at_time%5D=${
        endTime}&reservation_step_reservation_time%5Bgarage_id%5D=${
        reservation.garageId}`

    return reservationLinkNextDay
}

export function getReservationLinkSameGarage(reservation: Reservation, path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`

    const reservationLinkSameGarage = `${normalizedPath
        }?reservation_step_reservation_time%5Bgarage_id%5D=${reservation.garageId}`

    return reservationLinkSameGarage
}

export function mapReservation(reservation: ReservationFromServer): Reservation {
    return ({
        id: reservation.id,
        status: getReservationStatus(reservation.state, reservation.begins_at, reservation.ends_at),
        garageId: reservation.unit?.garage?.id,
        garageSlug: reservation.unit?.garage?.reservation_slug,
        garageName: reservation.unit?.garage?.name,
        garagePlace: reservation.unit?.parking_spot_by_garage,
        garage: {
            id: reservation.unit?.garage?.id,
            street: reservation.unit?.garage?.street,
            city: reservation.unit?.garage?.city,
            lat: reservation.unit?.garage?.lat,
            lng: reservation.unit?.garage?.lng,
            garage_translations: reservation.unit?.garage?.garage_translations?.length > 0 ?
                [...reservation.unit.garage.garage_translations] : [],
        },
        driverFullName: reservation.name,
        driverEmail: reservation.email,
        licensePlate: reservation.number_plate,
        phoneNumber1: reservation.phone_number,
        phoneNumber2: reservation.phone_number_2,
        startDate: reservation.begins_at,
        endDate: reservation.ends_at,
        createdAt: reservation.created_at,
        price: ((reservation.amount_after_coupon?.cents ?? reservation.amount_cents) / 100) + ((reservation.extra_payments_amount?.cents ?? 0) / 100),
        token: reservation.token,
        unpaidUrl: reservation.unpaid_url,
        finalUnpaidAmountType: normalizeFinalUnpaidAmountType(reservation.final_unpaid_amount_type),
        unpaidAmount: {
            cents: reservation.final_unpaid_amount?.cents ?? reservation.unpaid_amount?.cents,
            currency: reservation.final_unpaid_amount?.currency_iso ?? reservation.unpaid_amount?.currency_iso,
        },
        ...reservation.unit?.entrance?.id ? {
            entrance: {
                id: reservation.unit.entrance.id,
                gateClass: reservation.unit.entrance.class,
                isVisible: reservation.unit.entrance.is_visible_by_user,
                name: {
                    cs: reservation.unit.entrance.name,
                    en: reservation.unit.entrance.name_en,
                    de: reservation.unit.entrance.name_de,
                    pl: reservation.unit.entrance.name_pl,
                },
            }
        } : {},
        ...reservation.unit?.exit?.id ? {
            exit: {
                id: reservation.unit.exit.id,
                gateClass: reservation.unit.exit.class,
                isVisible: reservation.unit.exit.is_visible_by_user,
                name: {
                    cs: reservation.unit.exit.name,
                    en: reservation.unit.exit.name_en,
                    de: reservation.unit.exit.name_de,
                    pl: reservation.unit.exit.name_pl,
                },
            }
        } : {},
        ...reservation.unit?.garage?.reception_doors?.length > 0 ? {
            receptions: [...reservation.unit.garage.reception_doors.map(receptionGate => ({
                id: receptionGate.id,
                gateClass: receptionGate.class,
                isVisible: receptionGate.is_visible_by_user,
                name: {
                    cs: receptionGate.name,
                    en: receptionGate.name_en,
                    de: receptionGate.name_de,
                    pl: receptionGate.name_pl,
                },
            }))]
        } : {},
        invoices: reservation.invoices ? [...reservation.invoices] : [],
        tickets: reservation.tickets ? [...reservation.tickets] : [],
        creditNotes: reservation.credit_notes ? [...reservation.credit_notes] : [],
        invoiceCorrections: reservation.invoice_corrections ? [...reservation.invoice_corrections] : [],
        terms_of_services: {
            ...reservation.terms_of_services
        },
        privacy_policy: {
            ...reservation.privacy_policy
        },
        garage_tos: {
            ...reservation.garage_tos
        },
        change_unit_process_actual: reservation.change_unit_process_actual ? {
            id: reservation.change_unit_process_actual.id,
            successful_at: reservation.change_unit_process_actual.successful_at,
            failed_at: reservation.change_unit_process_actual.failed_at,
        } : null,
    })
}

export function isErrorResponse(data: unknown): data is ErrorResponse {
    if (typeof data !== 'object' || data === null) {
      return false
    }
    const record = data as Record<string, unknown>
    return typeof record.error === 'string'
}

export function isErrorArrayResponse(data: unknown): data is ErrorArrayResponse {
    if (typeof data !== 'object' || data === null) {
        return false
    }

    const record = data as Record<string, unknown>

    return Array.isArray(record.errors) && record.errors.every(error => typeof error === 'string')
}
